/*
 * Wishlist. The list lives in the browser (localStorage, key "vt-wishlist") so it works for guests and without
 * any network. Items are { id, handle }: the numeric product id and its handle.
 *
 * Logged-in customers also keep the list in a customer metafield. The theme hands the saved list to the page in
 * <script type="application/json" id="vt-wishlist-state"> (see snippets/vitto-wishlist-state.liquid) and every
 * change is POSTed to the Cloudflare Worker behind the app proxy (see wishlist-worker/):
 *
 *   { "loggedIn": true, "endpoint": "/apps/wishlist", "items": [{ "id": 1, "handle": "gucci-tee" }] }
 *
 * Which list wins, for a logged-in customer:
 *  - first load on a device (no "vt-wishlist-synced"): the guest list and the saved list are merged and the
 *    guest-only items are uploaded; only then is the device marked as synced;
 *  - later loads: the saved list replaces the local one, so a removal made on another device sticks, EXCEPT for
 *    changes made here that the saved list does not show yet ("vt-wishlist-pending", { id: 'add' | 'remove' }).
 *    Those survive a failed or cancelled save, or a page rendered before Shopify shows the write. They are sent
 *    again on each load until the saved list shows them, then dropped;
 *  - a guest load on a synced device means the shopper logged out: the list, the marker and the pending changes
 *    are cleared.
 *
 * Several tabs share one localStorage: every change re-reads it first, and a "storage" event refreshes this tab.
 *
 * Everything is inside one function so the theme gets no new globals. Tests load it with require().
 */
(() => {
  const STORAGE_KEY = 'vt-wishlist';
  const SYNCED_KEY = 'vt-wishlist-synced';
  const PENDING_KEY = 'vt-wishlist-pending';
  const STORAGE_VERSION = 1;
  const MAX_ITEMS = 100;
  const DEFAULT_ENDPOINT = '/apps/wishlist';
  const DEFAULT_TIMEOUT_MS = 8000;
  const STATE_ELEMENT_ID = 'vt-wishlist-state';

  /** Only the entries that are { id: positive integer, handle: non-empty string }, copied. */
  function cleanItems(entries) {
    if (!Array.isArray(entries)) return [];
    return entries
      .filter((entry) => entry && Number.isSafeInteger(entry.id) && entry.id > 0 && typeof entry.handle === 'string' && entry.handle !== '')
      .map((entry) => ({ id: entry.id, handle: entry.handle }));
  }

  /** The saved items from the raw localStorage text. Anything unreadable or malformed is skipped, never thrown. */
  function parseStored(raw) {
    let data;
    try {
      data = JSON.parse(raw);
    } catch (error) {
      return [];
    }
    if (!data || data.v !== STORAGE_VERSION) return [];
    return cleanItems(data.items);
  }

  /** The unconfirmed changes from the raw localStorage text, as a Map of product id -> 'add' | 'remove'. */
  function parsePending(raw) {
    const pending = new Map();
    let data;
    try {
      data = JSON.parse(raw);
    } catch (error) {
      return pending;
    }
    if (!data || data.v !== STORAGE_VERSION || !data.ops || typeof data.ops !== 'object') return pending;
    for (const [key, op] of Object.entries(data.ops)) {
      const id = Number(key);
      if (Number.isSafeInteger(id) && id > 0 && (op === 'add' || op === 'remove')) pending.set(id, op);
    }
    return pending;
  }

  /** `first` then `second`, one entry per id (the first one seen wins), the oldest dropped over `max`. */
  function unionItems(first, second, max = MAX_ITEMS) {
    const seen = new Set();
    const merged = [];
    for (const entry of [...first, ...second]) {
      if (seen.has(entry.id)) continue;
      seen.add(entry.id);
      merged.push({ id: entry.id, handle: entry.handle });
    }
    return merged.slice(-max);
  }

  function defaultStorage() {
    try {
      return window.localStorage;
    } catch (error) {
      return null; // blocked storage throws just for being touched
    }
  }

  class Wishlist extends EventTarget {
    #items = [];
    #storage;
    #max;
    #fetch;
    #endpoint;
    #timeoutMs;
    #loggedIn; // the page was rendered for a customer
    #canSend; // false once the server has refused us (401): no more saves on this page
    #serverItems;
    #pending = new Map();
    #queue = Promise.resolve();
    #syncing = null;

    constructor({
      storage = defaultStorage(),
      max = MAX_ITEMS,
      fetchFn = (...args) => fetch(...args),
      endpoint = DEFAULT_ENDPOINT,
      loggedIn = false,
      serverItems = [],
      timeoutMs = DEFAULT_TIMEOUT_MS,
    } = {}) {
      super();
      this.#storage = storage;
      this.#max = max;
      this.#fetch = fetchFn;
      this.#endpoint = endpoint;
      this.#timeoutMs = timeoutMs;
      this.#loggedIn = loggedIn;
      this.#canSend = loggedIn;
      this.#serverItems = cleanItems(serverItems);
      this.#refresh();
      // Another tab changed the shared storage.
      if (typeof window !== 'undefined' && window.addEventListener) window.addEventListener('storage', this.#onStorage);
    }

    items() {
      return this.#items.map((entry) => ({ ...entry }));
    }

    count() {
      return this.#items.length;
    }

    has(id) {
      return this.#items.some((entry) => entry.id === id);
    }

    add(entry) {
      this.#refresh();
      if (this.has(entry.id)) return;
      this.#items = unionItems(this.#items, [entry], this.#max);
      this.#note(entry.id, 'add');
      this.#changed();
      this.#send({ add: [entry.id] });
    }

    remove(id) {
      this.#refresh();
      if (!this.has(id)) return;
      this.#items = this.#items.filter((entry) => entry.id !== id);
      this.#note(id, 'remove');
      this.#changed();
      this.#send({ remove: [id] });
    }

    /** Saves the item if it is not saved, removes it if it is. Returns whether it is saved now. */
    toggle(entry) {
      this.#refresh();
      if (this.has(entry.id)) {
        this.remove(entry.id);
        return false;
      }
      this.add(entry);
      return true;
    }

    /** Resolves when every queued save has finished (they never reject). */
    idle() {
      return this.#queue.then(() => undefined);
    }

    /** Reconciles the local list with the saved one, once per page. See the rules at the top of the file. */
    sync() {
      this.#syncing ??= this.#reconcile();
      return this.#syncing;
    }

    async #reconcile() {
      const synced = this.#readStorage(SYNCED_KEY) === '1';
      this.#refresh();

      if (!this.#loggedIn) {
        if (synced) {
          this.#writeStorage(SYNCED_KEY, null);
          this.#pending = new Map();
          this.#replace([]);
          this.#writePending();
        }
        return;
      }

      // What the saved list shows now settles the changes we were waiting on.
      const serverIds = new Set(this.#serverItems.map((entry) => entry.id));
      for (const [id, op] of this.#pending) {
        if ((op === 'add') === serverIds.has(id)) this.#pending.delete(id);
      }

      // The saved list (plus, on the first load here, the guest list), with the unconfirmed changes laid over it.
      let merged = synced ? this.#serverItems : unionItems(this.#serverItems, this.#items, this.#max);
      for (const [id, op] of this.#pending) {
        if (op === 'remove') {
          merged = merged.filter((entry) => entry.id !== id);
        } else if (!merged.some((entry) => entry.id === id)) {
          const mine = this.#items.find((entry) => entry.id === id);
          if (mine) merged = unionItems(merged, [mine], this.#max);
        }
      }

      const mergedIds = new Set(merged.map((entry) => entry.id));
      const toAdd = merged.filter((entry) => !serverIds.has(entry.id)).map((entry) => entry.id);
      const toRemove = [...serverIds].filter((id) => !mergedIds.has(id));

      this.#replace(merged);
      this.#writePending();
      const sends = [];
      if (toAdd.length > 0) sends.push(this.#send({ add: toAdd }));
      if (toRemove.length > 0) sends.push(this.#send({ remove: toRemove }));
      const results = await Promise.all(sends);
      if (!synced && results.every(Boolean)) this.#writeStorage(SYNCED_KEY, '1');
    }

    /** Queues a save for a logged-in customer. Saves go out one at a time, in order. Resolves true if it was saved. */
    #send(body) {
      if (!this.#canSend) return Promise.resolve(false);

      const run = async () => {
        if (!this.#canSend) return false; // an earlier save was refused (401): stop for this page
        try {
          const options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            keepalive: true, // a tap followed by a click-through must still reach the server
          };
          // A hung request must not hold up every later save.
          if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) options.signal = AbortSignal.timeout(this.#timeoutMs);
          const response = await this.#fetch(this.#endpoint, options);
          if (response.status === 401) {
            this.#canSend = false;
            return false;
          }
          if (!response.ok) {
            console.warn(`Wishlist: saving failed with status ${response.status}`);
            return false;
          }
          return true;
        } catch (error) {
          console.warn('Wishlist: could not reach the server', error);
          return false;
        }
      };

      const result = this.#queue.then(run);
      this.#queue = result;
      return result;
    }

    /** Remembers a change a logged-in customer made here until the saved list shows it. The last change wins. */
    #note(id, op) {
      if (this.#loggedIn) this.#pending.set(id, op);
    }

    /** Takes the list and the unconfirmed changes from storage, if it is readable (another tab may have written). */
    #refresh() {
      const rawItems = this.#readStorage(STORAGE_KEY);
      if (rawItems === null) return;
      this.#items = unionItems([], parseStored(rawItems), this.#max);
      const rawPending = this.#readStorage(PENDING_KEY);
      this.#pending = rawPending === null ? new Map() : parsePending(rawPending);
    }

    #onStorage = (event) => {
      if (event.key !== null && event.key !== STORAGE_KEY && event.key !== PENDING_KEY) return;
      const before = this.#items.map((entry) => entry.id).join();
      this.#refresh();
      if (this.#items.map((entry) => entry.id).join() !== before) this.#announce();
    };

    #readStorage(key) {
      try {
        return this.#storage ? this.#storage.getItem(key) : null;
      } catch (error) {
        return null;
      }
    }

    /** Writes `value`, or removes the key when it is null. Blocked or full storage is ignored. */
    #writeStorage(key, value) {
      try {
        if (!this.#storage) return;
        if (value === null) this.#storage.removeItem(key);
        else this.#storage.setItem(key, value);
      } catch (error) {
        // The list still works for this page.
      }
    }

    #writePending() {
      if (this.#pending.size === 0) {
        this.#writeStorage(PENDING_KEY, null);
        return;
      }
      this.#writeStorage(PENDING_KEY, JSON.stringify({ v: STORAGE_VERSION, ops: Object.fromEntries(this.#pending) }));
    }

    /** Swaps in a whole list, announcing it only if it differs. */
    #replace(items) {
      const next = unionItems([], items, this.#max);
      const same = next.length === this.#items.length && next.every((entry, i) => entry.id === this.#items[i].id);
      if (same) return;
      this.#items = next;
      this.#changed();
    }

    #changed() {
      this.#writeStorage(STORAGE_KEY, JSON.stringify({ v: STORAGE_VERSION, items: this.#items }));
      this.#writePending();
      this.#announce();
    }

    #announce() {
      this.dispatchEvent(new CustomEvent('change', { detail: { items: this.items() } }));
    }
  }

  /** Builds the page's wishlist from the state element, publishes it as window.vittoWishlist and syncs it. */
  function bootWishlist(doc, overrides = {}) {
    let state = null;
    try {
      state = JSON.parse(doc.getElementById(STATE_ELEMENT_ID)?.textContent ?? 'null');
    } catch (error) {
      state = null;
    }
    state = state && typeof state === 'object' ? state : {};

    const list = new Wishlist({
      loggedIn: state.loggedIn === true,
      endpoint: typeof state.endpoint === 'string' && state.endpoint !== '' ? state.endpoint : DEFAULT_ENDPOINT,
      serverItems: state.items,
      ...overrides,
    });
    if (doc.defaultView) doc.defaultView.vittoWishlist = list;
    list.sync();
    return list;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { Wishlist, parseStored, parsePending, unionItems, bootWishlist, MAX_ITEMS };
  }

  if (typeof document !== 'undefined' && typeof window !== 'undefined' && !window.vittoWishlist) {
    bootWishlist(document);
  }

  /*
   * <wishlist-button data-product-id data-handle data-label-add data-label-remove><button data-saved>…
   * (see snippets/vitto-wishlist-button.liquid). Defined after the boot above, so window.vittoWishlist exists
   * by the time buttons already in the page upgrade; buttons added later (load more, quick view) work the same.
   */
  if (typeof customElements !== 'undefined' && typeof HTMLElement !== 'undefined' && !customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class WishlistButton extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          this.list = window.vittoWishlist;
          if (!this.button) return;
          this.button.addEventListener('click', this.onClick);
          if (this.list) this.list.addEventListener('change', this.render);
          this.render();
        }

        disconnectedCallback() {
          if (this.button) this.button.removeEventListener('click', this.onClick);
          if (this.list) this.list.removeEventListener('change', this.render);
        }

        // The heart usually sits over a card that is a link: saving must not open the product.
        onClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.list) this.list.toggle({ id: Number(this.dataset.productId), handle: this.dataset.handle });
        };

        render = () => {
          const saved = this.list ? this.list.has(Number(this.dataset.productId)) : false;
          // The label carries the state for assistive technology; data-saved is for the stylesheet.
          this.button.setAttribute('data-saved', String(saved));
          const label = this.querySelector('.vt-wishlist-button__label');
          if (label) label.textContent = saved ? this.dataset.labelRemove : this.dataset.labelAdd;
        };
      },
    );
  }

  /* <wishlist-count hidden>: the number of saved items in the header (empty and hidden at zero, "99+" above 99). */
  if (typeof customElements !== 'undefined' && typeof HTMLElement !== 'undefined' && !customElements.get('wishlist-count')) {
    customElements.define(
      'wishlist-count',
      class WishlistCount extends HTMLElement {
        connectedCallback() {
          this.list = window.vittoWishlist;
          if (!this.list) return;
          this.list.addEventListener('change', this.render);
          this.render();
        }

        disconnectedCallback() {
          if (this.list) this.list.removeEventListener('change', this.render);
        }

        render = () => {
          const count = this.list.count();
          this.textContent = count === 0 ? '' : count > 99 ? '99+' : String(count);
          this.hidden = count === 0;
        };
      },
    );
  }
})();
